import { Button } from "design-system/atoms/Button"
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card } from "design-system/molecules/Card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "design-system/molecules/Table"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { cn } from "design-system/utils"
import { formatDate, formatNumber } from "lopermut"
import { Check, X } from "lucide-react"
import { useImportCustomersMutation } from "modules/customers/hooks/useImportCustomersMutation"
import { useCountries } from "modules/shared/hooks/useCountries"
import { useState } from "react"
import { FieldValues, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  CustomerImportTarget,
  CustomerImportTargetEnum,
  ImportCustomersResponseData,
  ImporterData,
} from "schema"
import { toast } from "sonner"
import { z } from "zod"

type PreviewResponse = Extract<z.infer<typeof ImportCustomersResponseData>, { data: unknown }>

type Props = {
  fileForm: UseFormReturn<FieldValues>
  campaignId?: string
  actionResponse: PreviewResponse
  onBack: () => void
  onActionResponse: (response: z.infer<typeof ImportCustomersResponseData>) => void
  onQueued: (importId: string) => void
}

export function ImportReview({
  fileForm,
  campaignId,
  actionResponse,
  onBack,
  onActionResponse,
  onQueued,
}: Props) {
  const { t } = useTranslation()
  const { options: countries } = useCountries()
  const [targets, setTargets] = useState(() => actionResponse.columns.map(({ target }) => target))
  const [defaultCountry, setDefaultCountry] = useState(actionResponse.defaultCountry)

  const { mutateAsync: uploadCustomersImport, isPending: isUploading } = useImportCustomersMutation(
    {
      onSuccess: (result) => {
        if ("importId" in result) {
          onQueued(result.importId)
          return
        }
        onActionResponse(result)
      },
    },
  )

  const hasContactColumn = targets.some((target) => target === "email" || target === "phone")
  const { totalCount, previewCount, invalidRowsCount } = actionResponse
  const hasInvalidRows = !actionResponse.success && invalidRowsCount > 0
  const canContinue = hasContactColumn && invalidRowsCount < totalCount

  function onTargetChange(index: number, target: CustomerImportTarget) {
    const isField = target !== "metadata" && target !== "ignore"

    // A field maps to one column, so the column that had it falls back to metadata
    const nextTargets = targets.map((current, currentIndex) => {
      if (currentIndex === index) return target
      if (isField && current === target) return "metadata"
      return current
    })

    setTargets(nextTargets)
    refreshPreview({ nextTargets, nextDefaultCountry: defaultCountry })
  }

  function onDefaultCountryChange(nextDefaultCountry: string) {
    setDefaultCountry(nextDefaultCountry)
    refreshPreview({ nextTargets: targets, nextDefaultCountry })
  }

  function refreshPreview({
    nextTargets,
    nextDefaultCountry,
  }: {
    nextTargets: CustomerImportTarget[]
    nextDefaultCountry: string | null
  }) {
    uploadCustomersImport({
      preview: "true",
      file: fileForm.watch("file"),
      campaignId,
      columns: JSON.stringify(nextTargets),
      defaultCountry: nextDefaultCountry ?? undefined,
    })
  }

  async function onUpload() {
    toast.promise(
      uploadCustomersImport({
        preview: "false",
        file: fileForm.watch("file"),
        campaignId,
        columns: JSON.stringify(targets),
        defaultCountry: defaultCountry ?? undefined,
        skipInvalidRows: hasInvalidRows ? "true" : "false",
      }),
      {
        loading: t("customers.imports.progress.loading"),
        success: ({ data }) =>
          "isDuplicate" in data && data.isDuplicate
            ? t("customers.imports.progress.duplicate")
            : t("customers.imports.progress.queued"),
      },
    )
  }

  function renderCell(cell: z.infer<typeof ImporterData>) {
    const cellHasError = !!cell.error
    const cellIsEmpty = !cell.data || cell.data.length === 0
    const cellValue = cellHasError && cellIsEmpty ? t("shared.emptyField") : cell.data

    switch (cell.kind) {
      case "string":
      case "select":
        return <span>{cellValue}</span>
      case "number":
        return <span>{cellValue ? formatNumber({ amount: cellValue, fix: -1 }) : ""}</span>
      case "date":
        let formattedDate = ""
        if (cellValue && !isNaN(new Date(cellValue).getTime())) {
          formattedDate = formatDate(cellValue)
        }
        return <span>{formattedDate}</span>
      case "boolean":
        return <span>{cellValue ? <Check size={16} /> : <X size={16} />}</span>
      case "json":
        if (!cellValue || cellValue.length === 0) return <span>{"-"}</span>
        return (
          <Tooltip>
            <TooltipTrigger>
              <div className="max-w-40  overflow-hidden truncate bg-muted rounded-md p-1">
                <span className="text-xs font-mono">{cellValue}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-96 font-mono">{cellValue}</TooltipContent>
          </Tooltip>
        )
    }
  }

  const table = (
    <Card className={cn("transition-opacity", isUploading && "pointer-events-none opacity-60")}>
      <Table style={{ minWidth: `${actionResponse.columns.length * 180}px` }}>
        <TableHeader>
          <TableRow>
            {actionResponse.columns.map(({ title }, index) => (
              <TableHead key={index} className="h-auto py-3 align-top">
                <div className="flex min-w-36 flex-col gap-1.5">
                  <span className="truncate text-xs font-normal" title={title}>
                    {title}
                  </span>
                  <Select
                    value={targets[index]}
                    onValueChange={(value) =>
                      onTargetChange(index, CustomerImportTargetEnum.parse(value))
                    }
                  >
                    <SelectTrigger size="sm" className="text-foreground">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CustomerImportTargetEnum.options.map((target) => (
                        <SelectItem key={target} value={target}>
                          {t(`customers.imports.review.targets.${target}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {actionResponse.data.length === 0 && (
            <TableRow>
              <TableCell colSpan={actionResponse.columns.length}>
                {t("customers.imports.empty")}
              </TableCell>
            </TableRow>
          )}
          {actionResponse.data.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell, cellIndex) => {
                const cellHasError = !!cell.error

                const content = cellHasError ? (
                  <Tooltip>
                    <TooltipTrigger>{renderCell(cell)}</TooltipTrigger>
                    <TooltipContent>{cell.error}</TooltipContent>
                  </Tooltip>
                ) : (
                  renderCell(cell)
                )

                return (
                  <TableCell
                    key={cellIndex}
                    align={cell.align}
                    className={cn(
                      cellHasError && "text-error",
                      cell.key === "ignore" && "text-muted-foreground line-through",
                    )}
                  >
                    {content}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  )

  function getNote() {
    if (!hasContactColumn) return t("customers.imports.review.contactRequired")
    if (!actionResponse.success) {
      return t("customers.imports.review.previewError", {
        count: invalidRowsCount,
        total: totalCount,
        previewCount,
      })
    }

    return t("customers.imports.review.previewNote", {
      count: totalCount,
      previewCount: Math.min(previewCount, totalCount),
    })
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t("customers.imports.review.title")}</DialogTitle>
        <DialogDescription>{t("customers.imports.review.description")}</DialogDescription>
      </DialogHeader>
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
          <span className="text-sm text-muted-foreground">
            {t("customers.imports.review.defaultCountry")}
          </span>
          <Select value={defaultCountry ?? undefined} onValueChange={onDefaultCountryChange}>
            <SelectTrigger size="sm" className="sm:w-64">
              <SelectValue placeholder={t("customers.imports.review.defaultCountryPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {countries.map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-y-auto overflow-x-auto min-h-0">{table}</div>
        <span
          className={cn(
            "text-sm text-placeholder",
            (!actionResponse.success || !hasContactColumn) && "text-error",
          )}
        >
          {getNote()}
        </span>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onBack}>
          {t("shared.back")}
        </Button>
        <Button disabled={!canContinue || isUploading} onClick={onUpload}>
          {hasInvalidRows
            ? t("customers.imports.review.continueSkipping", { count: invalidRowsCount })
            : t("customers.imports.review.continue")}
        </Button>
      </DialogFooter>
    </>
  )
}
